/**
 * Peer Cleanup Orchestrator
 * Platform-agnostic cascade: removePeer(roleId) + strip roleId from all
 * conversations' participantRoleIds. Used by web (AppLayout.handleDeleteRole)
 * and CLI (contacts delete) so the cleanup rule lives in a single place.
 *
 * peer-sdk stays platform-neutral (no api/session dependency injected);
 * this orchestrator composes peerService + api at the app layer.
 */

export interface PeerCleanupDeps {
  peerService: {
    removePeer: (roleId: string) => Promise<void>
  }
  api: {
    setParticipants: (conversationId: string, participantRoleIds: string[]) => Promise<any>
  }
  conversationIndex: Array<{ id: string; participantRoleIds?: string[] }>
}

export interface PeerCleanupResult {
  /** Conversations whose participantRoleIds changed (for UI optimistic update). */
  updatedConversations: Array<{ id: string; participantRoleIds: string[] }>
}

/**
 * Remove a peer (LLM bot or worker role) and strip its roleId from every
 * conversation's participants. Idempotent: if a conversation does not
 * reference the roleId, it is left untouched.
 */
export async function removePeerWithCleanup(
  roleId: string,
  { peerService, api, conversationIndex }: PeerCleanupDeps,
): Promise<PeerCleanupResult> {
  await peerService.removePeer(roleId)

  const updatedConversations: Array<{ id: string; participantRoleIds: string[] }> = []
  for (const conv of conversationIndex) {
    if (conv.participantRoleIds?.includes(roleId)) {
      const next = conv.participantRoleIds.filter((id: string) => id !== roleId)
      await api.setParticipants(conv.id, next)
      updatedConversations.push({ id: conv.id, participantRoleIds: next })
    }
  }

  return { updatedConversations }
}
